import { Helmet } from 'react-helmet-async';
import { DEFAULT_OG_IMAGE, INDEX_FOLLOW, NOINDEX_FOLLOW, SITE_ORIGIN } from '@/lib/seo/constants';

export type RobotsDirective = typeof INDEX_FOLLOW | typeof NOINDEX_FOLLOW | 'noindex, nofollow';

export type PageSeoProps = {
  title: string;
  description?: string;
  /** Path only, e.g. `/faq` — builds canonical + og:url */
  path?: string;
  robots?: RobotsDirective;
  ogImage?: string;
};

function canonicalUrl(path: string): string {
  const normalized = path.startsWith('/') ? path : `/${path}`;
  return `${SITE_ORIGIN}${normalized}`;
}

/**
 * Per-route head tags for public/marketing pages.
 * SPA: crawlers that do not execute JS still see index.html defaults; this fixes
 * Google + in-app browsers and prepares for future prerender.
 */
export function PageSeo({
  title,
  description,
  path,
  robots = INDEX_FOLLOW,
  ogImage = DEFAULT_OG_IMAGE,
}: PageSeoProps) {
  const canonical = path ? canonicalUrl(path) : undefined;
  const desc = description?.trim();

  return (
    <Helmet>
      <title>{title}</title>
      {desc ? <meta name="description" content={desc} /> : null}
      <meta name="robots" content={robots} />
      {canonical ? <link rel="canonical" href={canonical} /> : null}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="HiroMatch" />
      <meta property="og:title" content={title} />
      {desc ? <meta property="og:description" content={desc} /> : null}
      {canonical ? <meta property="og:url" content={canonical} /> : null}
      <meta property="og:image" content={ogImage} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      {desc ? <meta name="twitter:description" content={desc} /> : null}
      <meta name="twitter:image" content={ogImage} />
    </Helmet>
  );
}
